import { formatShare } from '../ceo/ceoFormat'

export type MixSlice = {
  key: string
  label: string
  share: number
  tone?: 'current' | 'prior'
}

export type MixStack = {
  key: string
  label: string
  slices: MixSlice[]
}

export type MixBarsProps = {
  stacks: MixStack[]
  ariaLabel: string
  className?: string
  height?: number
}

export type MixGeometry = {
  stacks: Array<{
    key: string
    label: string
    x: number
    width: number
    slices: Array<{ key: string; label: string; y: number; height: number; share: number; fill: string }>
  }>
}

const FILLS = [
  'var(--ink, #1c1f26)',
  'var(--brand, #1b4965)',
  'var(--pos, #0e7c86)',
  'var(--anchor, #9aa0a8)',
  'var(--check-acc, #b7791f)',
]

export function computeMixStacks(stacks: MixStack[], width = 320, height = 180): MixGeometry {
  const padX = 24
  const padTop = 8
  const padBottom = 28
  const plotW = Math.max(1, width - padX * 2)
  const plotH = Math.max(1, height - padTop - padBottom)
  const slot = plotW / Math.max(stacks.length, 1)
  const barW = Math.min(48, slot * 0.55)
  return {
    stacks: stacks.map((stack, index) => {
      let y = padTop
      const slices = stack.slices.map((slice, sliceIndex) => {
        const heightPx = Math.max(1, slice.share * plotH)
        const next = { key: slice.key, label: slice.label, y, height: heightPx, share: slice.share, fill: FILLS[sliceIndex % FILLS.length] }
        y += heightPx
        return next
      })
      return {
        key: stack.key,
        label: stack.label,
        x: padX + slot * index + (slot - barW) / 2,
        width: barW,
        slices,
      }
    }),
  }
}

export function MixBars({ stacks, ariaLabel, className, height = 180 }: MixBarsProps) {
  const width = Math.max(240, stacks.length * 88)
  const geo = computeMixStacks(stacks, width, height)
  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      {geo.stacks.map((stack) => (
        <g key={stack.key}>
          {stack.slices.map((slice) => (
            <g key={slice.key}>
              <rect x={stack.x} y={slice.y} width={stack.width} height={slice.height} fill={slice.fill} />
              {slice.height > 16 ? (
                <text x={stack.x + stack.width + 6} y={slice.y + slice.height / 2 + 3} fill="var(--ink-2, #4c5360)" fontSize={10}>
                  {slice.label} {formatShare(slice.share)}
                </text>
              ) : null}
            </g>
          ))}
          <text x={stack.x + stack.width / 2} y={height - 8} textAnchor="middle" fill="var(--muted, #868c9a)" fontSize={10}>
            {stack.label}
          </text>
        </g>
      ))}
    </svg>
  )
}

export default MixBars
